import { DateTime } from 'luxon'

export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return [hours, minutes, remainingSeconds]
    .map((v) => v.toString().padStart(2, "0"))
    .join(":");
};

/** Format duration as hours and minutes only (e.g. "2h 30m") */
export const formatDurationHoursMinutes = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours === 0 && minutes === 0) return "0h 0m";
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  return parts.join(" ");
};

// Format time from ISO string to 12-hour Eastern Time
export const formatTime = (isoTime: string): string => {
  return DateTime.fromISO(isoTime)
    .setZone('America/New_York')
    .toFormat('h:mm a');
};

// Format date to show day name and date in Eastern Time
export const formatDate = (isoDate: string): string => {
  return DateTime.fromISO(isoDate)
    .setZone('America/New_York')
    .toFormat('EEEE, MMM d');
};

// Get week date range in Eastern Time (must match Luxon ISO week used by groupEntriesByWeek)
export const getWeekDateRange = (weekKey: string): { start: string; end: string } => {
  const [year, weekStr] = weekKey.split('-W');
  const weekNum = parseInt(weekStr, 10);
  if (!year || isNaN(weekNum)) return { start: '', end: '' };

  // Luxon ISO week format YYYY-Www = Monday of that ISO week (same as entryDate.weekNumber)
  const padded = weekStr.padStart(2, '0');
  const startOfWeek = DateTime.fromISO(`${year}-W${padded}`, { zone: 'America/New_York' });
  const endOfWeek = startOfWeek.plus({ days: 6 });

  return {
    start: startOfWeek.toFormat('MMMM d'),
    end: endOfWeek.toFormat('MMMM d')
  };
};

// Group timesheet entries by week
export const groupEntriesByWeek = (entries: any[]): Record<string, any[]> => {
  const weekGroups: Record<string, any[]> = {};
  
  entries.forEach((entry) => {
    const entryDate = DateTime.fromISO(entry.date);
    const weekKey = `${entryDate.year}-W${entryDate.weekNumber}`;
    
    if (!weekGroups[weekKey]) {
      weekGroups[weekKey] = [];
    }
    weekGroups[weekKey].push(entry);
  });

  // Sort entries within each week by date and start time (newest first)
  Object.keys(weekGroups).forEach((weekKey) => {
    weekGroups[weekKey].sort((a, b) => {
      const dateA = DateTime.fromISO(a.date);
      const dateB = DateTime.fromISO(b.date);
      
      if (dateA.equals(dateB)) {
        // If same date, sort by start time (newest first)
        const startTimeA = DateTime.fromISO(a.startTime);
        const startTimeB = DateTime.fromISO(b.startTime);
        return startTimeB.toMillis() - startTimeA.toMillis();
      }
      return dateB.toMillis() - dateA.toMillis();
    });
  });

  return weekGroups;
};

// Calculate total time for a week
export const getWeekTotal = (entries: any[]): number => {
  return entries.reduce((total, entry) => total + (entry.duration || 0), 0);
};

// Get user initials from first and last name
export const getUserInitials = (firstName?: string, lastName?: string): string => {
  const first = firstName || '';
  const last = lastName || '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
};
